import { isIOSDevice, isInAppBrowser } from "@/util/utils"

function getRuntimeWindow(browserWindow) {
  return browserWindow || (typeof window !== "undefined" ? window : null)
}

function getRuntimeNavigator(navigatorObject) {
  return navigatorObject || (typeof navigator !== "undefined" ? navigator : {})
}

export function isAndroidDevice(userAgent = "") {
  return /Android/i.test(userAgent)
}

export function resolvePhotoSaveAction({ userAgent = "", maxTouchPoints = 0 } = {}) {
  if (isIOSDevice(userAgent, maxTouchPoints)) {
    return "open-original"
  }

  if (isAndroidDevice(userAgent)) {
    return isInAppBrowser(userAgent) ? "open-chrome" : "open-original"
  }

  return "download"
}

export function openPhotoForManualSave(photoUrl, browserWindow) {
  const runtimeWindow = getRuntimeWindow(browserWindow)
  if (!runtimeWindow) return false

  const openedWindow = runtimeWindow.open(photoUrl, "_blank")
  if (openedWindow) {
    openedWindow.opener = null
    return true
  }

  runtimeWindow.location.assign(photoUrl)
  return false
}

export function openPhotoInAndroidChrome(photoUrl, browserWindow) {
  const runtimeWindow = getRuntimeWindow(browserWindow)
  if (!runtimeWindow) return false

  try {
    const target = new URL(photoUrl, runtimeWindow.location.href)
    if (!/^https?:$/.test(target.protocol)) {
      return openPhotoForManualSave(photoUrl, runtimeWindow)
    }

    const chromeTarget = new URL(target.href)
    if (chromeTarget.hash) {
      const hashParameters = new URLSearchParams(chromeTarget.hash.slice(1))
      hashParameters.forEach((value, key) => {
        if (!chromeTarget.searchParams.has(key)) {
          chromeTarget.searchParams.append(key, value)
        }
      })
      chromeTarget.hash = ""
    }

    const scheme = chromeTarget.protocol.slice(0, -1)
    const fallbackUrl = encodeURIComponent(target.href)
    const intentUrl = `intent://${chromeTarget.host}${chromeTarget.pathname}${chromeTarget.search}#Intent;scheme=${scheme};package=com.android.chrome;S.browser_fallback_url=${fallbackUrl};end`
    runtimeWindow.location.assign(intentUrl)
    return true
  } catch (error) {
    return openPhotoForManualSave(photoUrl, runtimeWindow)
  }
}

export function buildPhotoSaveGuideUrl({
  photoUrl,
  fileName = "lafia-photo.jpg",
  language = "ko",
  browserWindow,
} = {}) {
  if (!photoUrl) return ""

  const runtimeWindow = getRuntimeWindow(browserWindow)
  if (!runtimeWindow || !runtimeWindow.location) return ""

  try {
    const guideUrl = new URL("/photo-save/view.html", runtimeWindow.location.href)
    const parameters = new URLSearchParams()
    parameters.set("src", new URL(photoUrl, runtimeWindow.location.href).href)
    parameters.set("name", fileName)
    parameters.set("lang", language === "jp" ? "ja" : language)
    guideUrl.hash = parameters.toString()
    return guideUrl.href
  } catch (error) {
    return ""
  }
}

export function savePhotoForBrowser({
  photoUrl,
  fileName = "lafia-photo.jpg",
  guideUrl = "",
  browserWindow,
  navigatorObject,
} = {}) {
  if (!photoUrl) return false

  const runtimeWindow = getRuntimeWindow(browserWindow)
  const runtimeNavigator = getRuntimeNavigator(navigatorObject)
  if (!runtimeWindow) return false

  const userAgent = runtimeNavigator.userAgent || runtimeNavigator.vendor || ""
  const action = resolvePhotoSaveAction({
    userAgent,
    maxTouchPoints: runtimeNavigator.maxTouchPoints || 0,
  })
  const manualSaveUrl = guideUrl || photoUrl

  if (action === "open-chrome") {
    return openPhotoInAndroidChrome(manualSaveUrl, runtimeWindow)
  }

  if (action === "open-original") {
    return openPhotoForManualSave(manualSaveUrl, runtimeWindow)
  }

  try {
    const downloadLink = runtimeWindow.document.createElement("a")
    downloadLink.href = photoUrl
    downloadLink.download = fileName
    downloadLink.rel = "noopener"
    downloadLink.style.display = "none"
    runtimeWindow.document.body.appendChild(downloadLink)
    downloadLink.click()
    downloadLink.remove()
    return true
  } catch (error) {
    return openPhotoForManualSave(photoUrl, runtimeWindow)
  }
}
